import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Separator } from '@/shared/components/ui/separator';
import { MapPin, Clock, Phone } from 'lucide-react';
import { formatPrice } from '@/shared/utils/format-price';
import { formatPhone, formatWhatsApp } from '@/shared/utils/format-phone';
import type { MerchantDetails, MerchantScheduleData } from '../types/get-merchant-details';
import { useTracking } from '@/modules/analytics/hooks/use-tracking';

interface StoreDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  store: MerchantDetails | undefined;
}

export function StoreDetailsModal({ isOpen, onClose, store }: StoreDetailsModalProps) {
  const track = useTracking();
  if (!store) return <></>;

  // Parse do horário de funcionamento
  const parseScheduleData = (scheduleData: MerchantScheduleData | null | undefined) => {
    const days = {
      monday: 'Segunda',
      tuesday: 'Terça',
      wednesday: 'Quarta',
      thursday: 'Quinta',
      friday: 'Sexta',
      saturday: 'Sábado',
      sunday: 'Domingo',
    };

    // Se scheduleData for null ou undefined, retorna um array vazio
    if (!scheduleData) return [];
    
    return Object.entries(scheduleData).map(([day, times]) => ({
      day: days[day as keyof typeof days] || day,
      times: times || [],
    }));
  };

  const scheduleData = parseScheduleData(store.schedule_data || null);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] mx-auto max-h-[70vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-bold">Detalhes da Loja</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações de Contato */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900">Contato</h4>

            {store.contact?.phone && (
              <div className="flex items-center gap-3">
                <Phone fill="var(--night)" className="h-4 w-4 text-night" />
                <span className="text-sm">{formatPhone(store.contact.phone)}</span>
              </div>
            )}

            {store.contact?.whatsapp && (
              <div className="flex items-center gap-3">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                  alt="WhatsApp"
                  className="h-5 w-5"
                />
                <a
                  href={formatWhatsApp(store.contact.whatsapp).link}
                  onClick={() => track.lead('whatsapp')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm"
                >
                  {formatPhone(store.contact.whatsapp)}
                </a>
              </div>
            )}

            {store.contact?.cnpj && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500">CNPJ:</span>
                <span className="text-sm">{store.contact.cnpj}</span>
              </div>
            )}

            {/* Redes Sociais */}
            {(store.contact?.instagram || store.contact?.facebook) && (
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">Redes sociais</h4>

                {store.contact?.instagram && (
                  <div className="flex items-center gap-3">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg"
                      alt="Instagram"
                      className="h-5 w-5"
                    />
                    <a
                      href={`https://instagram.com/${store.contact.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:text-primary/80 transition-colors"
                    >
                      @{store.contact.instagram.replace('@', '')}
                    </a>
                  </div>
                )}

                {store.contact?.facebook && (
                  <div className="flex items-center gap-3">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/0/05/Facebook_Logo_%282019%29.png"
                      alt="Facebook"
                      className="h-5 w-5"
                    />
                    <a
                      href={`https://facebook.com/${store.contact.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:text-primary/80 transition-colors"
                    >
                      {store.contact.facebook}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Endereço */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900">Localização</h4>

            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                {store.location.address ? (
                  <>
                    <div className="text-sm">
                      {store.location.address.street || ''}, {store.location.address.city || ''} - {store.location.address.state || ''}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      CEP: {store.location.address.zip_code || ''} - {store.location.address.country || ''}
                    </div>
                  </>
                ) : (
                  <div className="text-sm">Endereço não disponível</div>
                )}
                {store.distance && store.distance > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Distância: {store.distance.toFixed(1)} km
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Horário de Funcionamento */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900">Horário de Funcionamento</h4>

            {scheduleData.length > 0 ? (
              <div className="space-y-2">
                {scheduleData.map(({ day, times }) => (
                  <div key={day} className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{day}:</span>
                      {times.map((time, index) => (
                        <span key={index} className="text-sm text-gray-600 ml-2">
                          {time.open} - {time.close}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-500">Horários não disponíveis</span>
              </div>
            )}
          </div>

          {/* Only show delivery information if merchant supports delivery (delivery_type !== 2) */}
          {store.delivery_type !== 2 && (
            <>
              <Separator />

              {/* Informações de Entrega */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Entrega</h4>

                <div className="space-y-2">
                  {store.delivery_time && store.delivery_time.min && store.delivery_time.max && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Tempo de entrega:</span>
                      <span className="text-sm font-medium">
                        {store.delivery_time.min} - {store.delivery_time.max} min
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Valor mínimo:</span>
                    <span className="text-sm font-medium">{formatPrice(store.min_order_price)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Taxa de entrega:</span>
                    <span className="text-sm font-medium">{formatPrice(store.delivery_charge.toString())}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Show pickup information when merchant only supports pickup */}
          {store.delivery_type === 2 && (
            <>
              <Separator />

              {/* Informações de Retirada */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Retirada</h4>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Valor mínimo:</span>
                    <span className="text-sm font-medium">{formatPrice(store.min_order_price)}</span>
                  </div>

                  {/* Tempo de retirada com cópia sugestiva em destaque (issue 229). */}
                  {store.preparation_time != null && (
                    <div className="text-sm font-semibold text-primary">
                      Retire na loja em apenas {store.preparation_time} minutos
                    </div>
                  )}

                  <div className="text-sm text-gray-600">
                    Retire seu pedido diretamente na loja
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
